/**
 * Operator override ของ patient × branch
 *
 * Persistence: BE column patient_branches.branchVisitStatusOverride (per (patientId, branchId))
 * Cache: localStorage `BRANCH_VISIT_STATUS:{patientId}:{branchId}` — optimistic UI + offline fallback
 *
 * Flow:
 *   1. Read: localStorage ก่อน, fallback ค่าจาก patient payload (server side)
 *   2. Write: localStorage ก่อน (optimistic) → API → rollback ถ้า fail
 *   3. Hydrate: ตอน load patient จาก server เอาค่ามาจาก patient.patientBranches[].branchVisitStatusOverride
 */

import RegisterApi from 'api/counter/register.api'

const STORAGE_PREFIX = 'BRANCH_VISIT_STATUS'

const localKey = (patientId: number, branchId: number) =>
  `${STORAGE_PREFIX}:${patientId}:${branchId}`

export type BranchVisitStatusValue = 'NEW' | 'EXISTING'

export interface BranchVisitOverrideRecord {
  status: BranchVisitStatusValue
  decidedAt: string
}

export const getBranchVisitOverride = (
  patientId: number,
  branchId: number
): BranchVisitOverrideRecord | null => {
  if (!patientId || !branchId) return null
  try {
    const raw = localStorage.getItem(localKey(patientId, branchId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed?.status !== 'NEW' && parsed?.status !== 'EXISTING') return null
    return parsed as BranchVisitOverrideRecord
  } catch {
    return null
  }
}

const writeLocal = (
  patientId: number,
  branchId: number,
  status: BranchVisitStatusValue,
  decidedAtIso?: string
) => {
  try {
    const value: BranchVisitOverrideRecord = {
      status,
      decidedAt: decidedAtIso || new Date().toISOString()
    }
    localStorage.setItem(localKey(patientId, branchId), JSON.stringify(value))
  } catch {
    /* localStorage unavailable (private mode, quota) — ไม่ throw, ให้ fallback ใช้ server value ตอน reload */
  }
}

const clearLocal = (patientId: number, branchId: number) => {
  try {
    localStorage.removeItem(localKey(patientId, branchId))
  } catch {
    /* ignore */
  }
}

/**
 * Sync localStorage cache กับค่าที่ server ส่งมาตอน load patient
 * เรียกใน loadPatient() หลัง setPatient(...)
 */
export const hydrateBranchVisitOverrideFromPatient = (
  patientId: number,
  branchId: number,
  serverOverride: BranchVisitStatusValue | null | undefined,
  serverDecidedAt: string | Date | null | undefined
) => {
  if (!patientId || !branchId) return
  if (serverOverride === 'NEW' || serverOverride === 'EXISTING') {
    const iso = serverDecidedAt ? new Date(serverDecidedAt).toISOString() : new Date().toISOString()
    writeLocal(patientId, branchId, serverOverride, iso)
  } else {
    clearLocal(patientId, branchId)
  }
}

export interface PersistBranchVisitOverrideExtras {
  firstVisitDate?: string | null
  note?: string | null
}

/**
 * Persist ไปที่ BE + optimistic update localStorage
 * Rollback localStorage ถ้า API fail
 *
 * extras (firstVisitDate / note) บันทึกเฉพาะตอน status === 'EXISTING'
 */
export const persistBranchVisitOverride = async (
  patientId: number,
  branchId: number,
  status: BranchVisitStatusValue,
  extras?: PersistBranchVisitOverrideExtras
): Promise<void> => {
  if (!patientId || !branchId) return
  const previous = getBranchVisitOverride(patientId, branchId)
  writeLocal(patientId, branchId, status)
  try {
    await RegisterApi.updateBranchVisitStatusOverride(patientId, {
      status,
      firstVisitDate: extras?.firstVisitDate ?? null,
      note: extras?.note ?? null
    })
  } catch (e) {
    if (previous) {
      writeLocal(patientId, branchId, previous.status, previous.decidedAt)
    } else {
      clearLocal(patientId, branchId)
    }
    throw e
  }
}
